import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, CheckCircle2, XCircle, KeyRound, Phone, RefreshCw, ExternalLink } from "lucide-react";
import api from "../lib/api";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";
const labelCls = "font-mono text-[10px] uppercase tracking-wider text-[#8696A0]";
const card = "border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-4";
const btnPrimary = "inline-flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-2 border border-[#CBD5E1] text-[#374151] hover:bg-[#E9EDEF] rounded-lg px-3 py-1.5 text-sm transition-colors disabled:opacity-50";
const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

const FIELD_LABELS = {
  account_sid: "Account SID", auth_token: "Auth Token", auth_id: "Auth ID", api_key: "API Key",
  api_token: "API Token", subdomain: "API subdomain (e.g. api.exotel.com / api.in.exotel.com)",
  app_id: "Flow App ID (contains the Voicebot applet; for outbound)",
};
const HELP = {
  twilio: "console.twilio.com → Account info. Buy or port a number with voice. Indian numbers need KYC.",
  plivo: "console.plivo.com → Overview. Create an Application with the Answer URL from Voice Calls → Phone.",
  vobiz: "console.vobiz.ai → Dashboard shows Auth ID and Auth Token. Create an Application with the Answer URL from Voice Calls → Phone and attach your number to it.",
  exotel: "my.exotel.com → API settings. Build a flow with a Voicebot applet using the URL from Voice Calls → Phone.",
};

function TelephonyCard() {
  const [cat, setCat] = useState(null);
  const [cur, setCur] = useState(null);
  const [prov, setProv] = useState("");
  const [creds, setCreds] = useState({});
  const [numbers, setNumbers] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/voice/catalog").then((r) => setCat(r.data.telephony));
    api.get("/voice/telephony").then((r) => {
      setCur(r.data);
      setProv(r.data.provider || "");
      setCreds(r.data.creds || {});
      setNumbers((r.data.numbers || []).join("\n"));
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/voice/telephony", { provider: prov, creds, numbers: numbers.split("\n").map((x) => x.trim()).filter(Boolean) });
      setCur(r.data);
      r.data.test?.ok ? toast.success("Phone provider connected") : toast.error(r.data.test?.detail || "Saved, but the test failed");
    } catch (e) { toast.error(errText(e, "Save failed")); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const r = await api.post("/voice/telephony/test"); setCur(r.data); r.data.test?.ok ? toast.success("Connected") : toast.error(r.data.test?.detail); }
    catch (e) { toast.error(errText(e, "Test failed")); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the phone connection? Calls to and from your number will stop.")) return;
    await api.delete("/voice/telephony"); toast.success("Removed"); load();
  };
  if (!cat || !cur) return <div className={card}><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const spec = cat[prov] || { fields: [], secret: [] };
  return (
    <div className={card} data-testid="voice-telephony">
      <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#075E54]" /><h3 className="font-heading text-sm">Phone calls (telephony provider)</h3>
        {cur.provider && (cur.connected
          ? <span className="ml-auto text-xs text-[#075E54] flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> connected</span>
          : <span className="ml-auto text-xs text-red-600 flex items-center gap-1"><XCircle className="h-3 w-3" /> not verified</span>)}
      </div>
      <p className="text-xs text-[#8696A0]">Needed only for real phone calls. Website and test calls work without it. Your own account is billed by the provider.</p>
      <div>
        <p className={labelCls}>Provider</p>
        <select className={inputCls + " mt-1"} value={prov} onChange={(e) => { setProv(e.target.value); setCreds({}); }} data-testid="voice-tel-provider">
          <option value="">— choose —</option>
          {Object.entries(cat).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {prov && <p className="text-xs text-[#8696A0] mt-1">{HELP[prov]}</p>}
      </div>
      {spec.fields.map((f) => (
        <div key={f}>
          <p className={labelCls}>{FIELD_LABELS[f] || f}</p>
          <input className={inputCls + " mt-1 font-mono"} type={spec.secret.includes(f) ? "password" : "text"} value={creds[f] || ""}
            placeholder={spec.secret.includes(f) && cur.creds?.[f] ? `${cur.creds[f]} (leave to keep)` : ""}
            onChange={(e) => setCreds({ ...creds, [f]: e.target.value })} data-testid={`voice-tel-${f}`} />
        </div>
      ))}
      {prov && (
        <div>
          <p className={labelCls}>Your phone number(s), with country code — one per line</p>
          <textarea rows={2} className={inputCls + " mt-1 font-mono"} placeholder="+919111895557" value={numbers} onChange={(e) => setNumbers(e.target.value)} data-testid="voice-tel-numbers" />
        </div>
      )}
      {cur.last_test && !cur.connected && <p className="text-xs text-red-600">{cur.last_test.detail}</p>}
      <div className="flex gap-2">
        <button className={btnPrimary} onClick={save} disabled={busy || !prov} data-testid="voice-tel-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; test</button>
        {cur.provider && <button className={btnGhost} onClick={test} disabled={busy}><RefreshCw className="h-4 w-4" /> Test</button>}
        {cur.provider && <button className={btnGhost} onClick={remove}><Trash2 className="h-4 w-4" /> Remove</button>}
      </div>
    </div>
  );
}

function KeyRow({ k, reload }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/voice/keys", { provider: k.provider, key: val, test: true });
      setVal("");
      const t = r.data.test || {};
      t.ok === false ? toast.error(t.detail) : toast.success(t.state === "untested" ? "Saved (no automatic test for this provider)" : "Key works");
      reload();
    } catch (e) { toast.error(errText(e, "Save failed")); } finally { setBusy(false); }
  };
  const del = async () => { await api.delete(`/voice/keys?provider=${k.provider}`); toast.success("Removed"); reload(); };
  return (
    <div className="border-t border-[#E9EDEF] pt-3 first:border-0 first:pt-0" data-testid={`voice-key-${k.provider}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[#111B21]">{k.label}</span>
        {k.get_url && <a href={k.get_url} target="_blank" rel="noreferrer" className="text-[#8696A0] hover:text-[#075E54]"><ExternalLink className="h-3 w-3" /></a>}
        <span className="ml-auto text-xs text-[#8696A0]">
          {k.own ? `your key ••••${k.own.last4}` : k.platform_available ? "using platform key" : "no key"}
        </span>
      </div>
      <div className="flex gap-2 mt-2">
        <input type="password" className={inputCls + " font-mono"} placeholder={k.own ? "Replace key" : "Paste your key (optional)"} value={val} onChange={(e) => setVal(e.target.value)} />
        <button className={btnGhost} onClick={save} disabled={busy || val.length < 8}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</button>
        {k.own && <button className={btnGhost} onClick={del}><Trash2 className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

function KeysCard() {
  const [keys, setKeys] = useState(null);
  const load = useCallback(() => api.get("/voice/keys").then((r) => setKeys(r.data)).catch(() => setKeys([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className={card} data-testid="voice-keys">
      <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-[#075E54]" /><h3 className="font-heading text-sm">Voice AI connections (API keys)</h3></div>
      <p className="text-xs text-[#8696A0]">Optional. Add your own keys to bill voice usage to your accounts; otherwise the platform's keys are used where available. Keys are encrypted and never shown again.</p>
      {!keys ? <Loader2 className="h-4 w-4 animate-spin" /> : keys.map((k) => <KeyRow key={k.provider} k={k} reload={load} />)}
    </div>
  );
}

export default function VoiceConnections() {
  return (
    <div className="space-y-6 max-w-2xl">
      <TelephonyCard />
      <KeysCard />
    </div>
  );
}
